import { useState, useEffect } from 'react';
import apiClient from '../services/apiClient';

// Fetches the franchise directory (admin-only) and returns a pincode -> franchise lookup.
// Pass `enabled=false` (default) to skip the fetch entirely, e.g. when rendered on the
// franchise's own dashboard where showing "which franchise" would be redundant.
const useFranchiseLookup = (enabled = false) => {
    const [franchises, setFranchises] = useState([]);

    useEffect(() => {
        if (!enabled) return;
        const fetchFranchises = async () => {
            try {
                const token = localStorage.getItem('token');
                const res = await apiClient.get('/franchise', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setFranchises(res.data);
            } catch (error) {
                console.error('Failed to fetch franchises:', error);
            }
        };
        fetchFranchises();
    }, [enabled]);

    const getFranchiseForPincode = (pincode) => franchises.find(f => f.pincodesCovered?.includes(pincode));

    // Groups a list of items into separate per-franchise buckets (no mixing), using
    // getPincode(item) to resolve each item's pincode. Items with no matching franchise
    // are bucketed under a single "No Franchise (Direct)" group, listed last.
    const groupByFranchise = (items, getPincode) => {
        const groups = {};
        const unclaimed = { key: 'unclaimed', label: 'No Franchise (Direct)', franchise: null, items: [] };

        items.forEach(item => {
            const pincode = getPincode(item);
            const franchise = getFranchiseForPincode(pincode);
            if (!franchise) {
                unclaimed.items.push(item);
                return;
            }
            if (!groups[franchise._id]) {
                groups[franchise._id] = { key: franchise._id, label: franchise.name, franchise, items: [] };
            }
            groups[franchise._id].items.push(item);
        });

        const sortedGroups = Object.values(groups).sort((a, b) => a.label.localeCompare(b.label));
        return unclaimed.items.length > 0 ? [...sortedGroups, unclaimed] : sortedGroups;
    };

    // Restricts a list of items to just the ones belonging to a single franchise ID —
    // used when a specific franchise's own card asks to see only its own orders/returns.
    const filterByFranchiseId = (items, getPincode, franchiseId) => {
        if (!franchiseId) return items;
        return items.filter(item => getFranchiseForPincode(getPincode(item))?._id === franchiseId);
    };

    return { getFranchiseForPincode, groupByFranchise, filterByFranchiseId };
};

export default useFranchiseLookup;
